import { sanitizeUrl } from "@braintree/sanitize-url";
import { TYPE } from "three-ammo/constants";
import { COLLISION_LAYERS } from "../constants";

// Registers a lightweight wrapper that converts a simple `spawner` declaration
// on an element into the richer set of components required for runtime spawning.
// This is equivalent to the logic that gets applied when GLTF files are inflated
// (see `gltf-component-mappings.js`). Having it as a standalone component means
// authors can now add `<a-entity spawner="src: ..."></a-entity>` directly in
// markup or at runtime and get the same behaviour.
AFRAME.registerComponent("spawner", {
  schema: {
    /** URL to the asset that will be spawned */
    src: { type: "string" },
    /** Whether the URL should be run through the media resolver service */
    resolve: { type: "boolean", default: true },
    /** Optional media-loader options that override defaults */
    mediaOptions: {
      default: {},
      parse: v => (typeof v === "object" ? v : JSON.parse(v)),
      stringify: JSON.stringify
    }
  },

  init() {
    this.applySpawnerBehaviour();
    this.el.object3D.scriptableType = "spawner";
  },

  /**
   * Called when component data changes. We only re-apply the behaviour if any
   * of the user-supplied properties actually changed. This prevents an
   * infinite update loop caused by re-setting attributes that didn't change.
   */
  update(oldData) {
    if (!oldData) {
      // First run after init.
      return;
    }

    const propsChanged =
      this.data.src !== oldData.src ||
      this.data.resolve !== oldData.resolve ||
      JSON.stringify(this.data.mediaOptions) !== JSON.stringify(oldData.mediaOptions);

    if (propsChanged) {
      this.applySpawnerBehaviour();
    }
  },

  applySpawnerBehaviour() {
    const { src, resolve, mediaOptions } = this.data;
    if (!src) return;

    const sanitizedSrc = sanitizeUrl(src);

    // Loader needed so users get a thumbnail / placeholder while the GLB loads.
    this.el.setAttribute("media-loader", {
      src: sanitizedSrc,
      resolve,
      fileIsOwned: true,
      animate: false,
      moveTheParentNotTheMesh: true,
      useFancyLoader: false
    });

    // Mark as interactable so it responds to cursor / hand events.
    this.el.setAttribute("css-class", "interactable");

    // The heavy-lifting of actually spawning happens in `super-spawner`.
    this.el.setAttribute("super-spawner", {
      src: sanitizedSrc,
      resolve,
      template: "#interactable-media",
      mediaOptions: mediaOptions || {}
    });

    // Nice hover feedback.
    this.el.setAttribute("hoverable-visuals", "");

    // Physics so that users can't push the spawner away.
    this.el.setAttribute("body-helper", {
      mass: 0,
      type: TYPE.STATIC,
      collisionFilterGroup: COLLISION_LAYERS.INTERACTABLES,
      collisionFilterMask: COLLISION_LAYERS.DEFAULT_SPAWNER
    });

    // Needed so other clients can highlight the spawner on hover.
    this.el.setAttribute("is-remote-hover-target", "");

    // Tag used by hand collision system.
    this.el.setAttribute("tags", { isHandCollisionTarget: true });
  }
});
